import { useState, useEffect, type ReactNode } from 'react';
import styled from 'styled-components';

import { ChevronDownIc, ChevronUpIc } from '@dsch/dd-icons';

import { WhitePanel } from '../WhitePanel/WhitePanel';
import { InfoTitle, type InfoTitleProps } from '../InfoTitle/InfoTitle';
import {
  OpeningHours,
  type OpeningHourProps,
} from '../OpeningHours/OpeningHours';
import { Link } from '../Button/Link';
import { OptionalNextLink } from '../OptionalNextLink/OptionalNextLink';
import { GoogleReviewPanel } from '../../../components/PageWrappers/AdDetails/components/GoogleReviews/GoogleReviewPanel/GoogleReviewPanel';

import type { GoogleReviews } from '../../../components/PageWrappers/AdDetails/types/';

import { Spacer } from '../../../helpers/genericStyles';
import {
  updateOpeningHours,
  isAppointmentOnly,
  getOpeningHoursForDay,
} from '../../../helpers/openingHours';
import { CDN_STATIC_ASSETS } from '../../../utils';
import { donedealBreakpoints } from 'styled-system';
const { media } = donedealBreakpoints;

type OpeningHours = {
  day:
    | 'Monday'
    | 'Tuesday'
    | 'Wednesday'
    | 'Thursday'
    | 'Friday'
    | 'Saturday'
    | 'Sunday';
  openTime?: string;
  closeTime?: string;
  appointmentOnly?: boolean;
  appointmentOnlyText?: string;
};

export interface DealerPanelProps
  extends Omit<InfoTitleProps, 'illustration' | 'linkHref'> {
  smallLogo?: string;
  affiliateLogo?: string;
  showRoomImage?: string;
  largeLogo?: string;
  renderMakeEnquiry?: ReactNode;
  renderShowPhone?: ReactNode;
  showroomUrl: string;
  location?: string;
  address?: string;
  longitude?: number;
  latitude?: number;
  openingHours?: OpeningHours[];
  websiteUrl?: string;
  googleReviews?: GoogleReviews;
  adId: number;
}

const SWhitePanel = styled(WhitePanel)`
  padding: 0;
`;

const HeadingContainer = styled.div<{ isShowRoomImage: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  padding-top: ${({ theme }) => theme.spacing.M16};
  padding-bottom: ${({ theme, isShowRoomImage }) =>
    isShowRoomImage ? '0' : theme.spacing.S12};
  margin: ${({ theme }) => `0 ${theme.spacing.M16}`};
  margin-bottom: ${({ theme, isShowRoomImage }) =>
    isShowRoomImage ? theme.spacing.S12 : theme.spacing.M16};
  ${({ theme, isShowRoomImage }) =>
    !isShowRoomImage && `border-bottom: 1px solid ${theme.colors.GREY_LIGHT}`};

  ${media.medium} {
    margin: ${({ theme }) => `0 ${theme.spacing.M24}`};
    ${({ theme, isShowRoomImage }) =>
      !isShowRoomImage
        ? `margin-bottom: ${theme.spacing.M24};`
        : `margin-bottom: ${theme.spacing.S12};`}
  }
`;

const AffiliateLogo = styled.div`
  width: 36px;
  margin-left: ${({ theme }) => theme.spacing.S8};

  img {
    width: 100%;
    height: auto;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  top: 0;
  left: 0;
  height: 80px;
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  ${media.medium} {
    height: 190px;
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const DealerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const Logo = styled.img`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing.S8};
  left: ${({ theme }) => theme.spacing.M16};
  width: 64px;
  height: 32px;
  z-index: 50;

  ${media.medium} {
    width: 160px;
    height: 80px;
    left: ${({ theme }) => theme.spacing.M24};
  }
`;

const InformationContainer = styled.div`
  padding: ${({ theme }) =>
    `0 ${theme.spacing.M16} ${theme.spacing.M16} ${theme.spacing.M16}`};
  display: grid;

  ${media.medium} {
    padding: ${({ theme }) =>
      `0 ${theme.spacing.M24} ${theme.spacing.M24} ${theme.spacing.M24}`};
    grid-template-columns: 1fr 1fr;
    column-gap: ${({ theme }) => theme.spacing.M24};
  }
`;

const ButtonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.S8};
`;

const ContactList = styled.div``;

const ListRow = styled.div<{ isOpen?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  margin-bottom: ${({ theme, isOpen }) =>
    isOpen ? theme.spacing.S8 : theme.spacing.M16};

  &:last-child {
    margin-bottom: 0;
  }
`;

const Key = styled.p<{ isOpen?: string }>`
  ${({ theme }) => theme.fontSize.B14};
  color: ${({ theme, isOpen }) =>
    isOpen === 'Open Now'
      ? theme.colors.GREEN_DARK
      : isOpen === 'CLOSED'
      ? theme.colors.RED_DARK
      : theme.colors.GREY_DARK};
  margin-right: ${({ theme }) => theme.spacing.M16};
`;

const Value = styled.p`
  ${({ theme }) => theme.fontSize.B14};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  word-break: break-all;
  text-align: right;
`;

const SOpeningHours = styled(OpeningHours)`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
`;

const LocationContainer = styled.div`
  text-align: right;
  background-color: ${({ theme }) => theme.colors.OFFWHITE};
  padding: ${({ theme }) => `${theme.spacing.S8} ${theme.spacing.M16}`};
  margin-bottom: ${({ theme }) => theme.spacing.S8};
`;

const Divider = styled.div`
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.GREY_LIGHT}`};
  margin: ${({ theme }) =>
    `0 ${theme.spacing.M16} ${theme.spacing.M16} ${theme.spacing.M16}`};

  ${media.large} {
    margin: ${({ theme }) =>
      `0 ${theme.spacing.M24} ${theme.spacing.M24} ${theme.spacing.M24}`};
  }
`;

const ButtonListAndVisitShowroomLinkContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  text-align: center;

  ${media.medium} {
    margin-bottom: 0;
  }
`;

const DealerPanel = (props: DealerPanelProps) => {
  const {
    title,
    smallLogo,
    affiliateLogo,
    showRoomImage,
    largeLogo,
    renderMakeEnquiry,
    renderShowPhone,
    showroomUrl,
    location,
    address,
    longitude,
    latitude,
    openingHours,
    websiteUrl,
    googleReviews,
    adId,
    listItems,
  } = props;

  const [isLocationOpen, setIsLocationOpen] = useState<boolean>(false);
  const [isDisplayTimesOpen, setIsDisplayTimesOpen] = useState<boolean>(false);
  const [appointmentOnly, setAppointmentOnly] = useState<boolean>(false);
  const [displayTimeKey, setDisplayTimeKey] = useState<string>('Opening hours');
  const [displayTime, setDisplayTime] = useState<string>('');
  const [updatedOpeningHours, setUpdatedOpeningHours] =
    useState<OpeningHourProps[]>();

  useEffect(() => {
    // TODO: Handle this error better and also log to GTM?
    try {
      setDisplayTime(getOpeningHoursForDay(openingHours));
      setAppointmentOnly(isAppointmentOnly(openingHours));
      const { mappedOpeningHours, displayTime } =
        updateOpeningHours(openingHours);
      setDisplayTimeKey(displayTime);
      setUpdatedOpeningHours(mappedOpeningHours);
    } catch (error) {
      console.error('Opening hours could not be rendered due to error.', error);
    }
    // TODO Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SWhitePanel
      className="common-margin-bottom"
      data-testid="bottom-seller-panel"
    >
      <OptionalNextLink href={showroomUrl}>
        <HeadingContainer
          isShowRoomImage={Boolean(showRoomImage)}
          id="dealer-panel"
        >
          <InfoTitle
            title={title}
            listItems={listItems}
            illustration={smallLogo}
            shouldTruncate
          />
          {affiliateLogo && (
            <AffiliateLogo>
              <img src={affiliateLogo} alt={`Affiliate logo`} />
            </AffiliateLogo>
          )}
        </HeadingContainer>
        {showRoomImage && (
          <ImageContainer>
            <DealerImage src={showRoomImage} />
            {largeLogo && <Logo src={largeLogo} />}
          </ImageContainer>
        )}
      </OptionalNextLink>
      <InformationContainer>
        <ButtonListAndVisitShowroomLinkContainer>
          <ButtonList>
            {renderMakeEnquiry}
            {renderShowPhone}
          </ButtonList>
          {showroomUrl && (
            <>
              <Spacer marginBottom="M16" />
              <Link ofType="SECONDARY" underline href={showroomUrl}>
                Visit showroom
              </Link>
            </>
          )}
        </ButtonListAndVisitShowroomLinkContainer>
        <ContactList>
          <ListRow
            onClick={() => setIsLocationOpen(!isLocationOpen)}
            isOpen={isLocationOpen}
          >
            <Key>Location</Key>
            <Value data-testid="dealer-panel-location">
              {location}{' '}
              {isLocationOpen ? (
                <ChevronUpIc width="16" height="16" />
              ) : (
                <ChevronDownIc width="16" height="16" />
              )}
            </Value>
          </ListRow>
          {isLocationOpen && (
            <LocationContainer>
              {latitude && longitude && (
                <Link
                  ofType="SECONDARY"
                  fontSize="B12"
                  href={`https://www.google.com/maps/place/${latitude},${longitude}/@${latitude},${longitude}z/`}
                  target="_blank"
                  rel="noopener nofollow"
                  data-tracking={'visit-google-reviews-link-location-dropdown'}
                >
                  {address}
                </Link>
              )}
            </LocationContainer>
          )}
          {updatedOpeningHours && (
            <ListRow
              onClick={() =>
                !appointmentOnly && setIsDisplayTimesOpen(!isDisplayTimesOpen)
              }
              isOpen={isDisplayTimesOpen}
            >
              <Key isOpen={displayTimeKey}>{displayTimeKey}</Key>
              <Value>
                {displayTime}{' '}
                {!appointmentOnly &&
                  (isDisplayTimesOpen ? (
                    <ChevronUpIc width="16" height="16" />
                  ) : (
                    <ChevronDownIc width="16" height="16" />
                  ))}
              </Value>
            </ListRow>
          )}
          {isDisplayTimesOpen && (
            <SOpeningHours openingHours={updatedOpeningHours} />
          )}
          {websiteUrl && (
            <ListRow>
              <Key>Website</Key>
              <Value>
                <Link
                  fontSize="B14"
                  href={websiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                >
                  {websiteUrl}
                </Link>
              </Value>
            </ListRow>
          )}
        </ContactList>
      </InformationContainer>

      {googleReviews?.reviewList &&
        googleReviews.reviewList.length >= 1 &&
        googleReviews.displayReviews && (
          <>
            <Divider />
            <GoogleReviewPanel
              title={title}
              googleReviews={googleReviews}
              cdnUrl={CDN_STATIC_ASSETS}
              adId={adId}
            />
          </>
        )}
    </SWhitePanel>
  );
};

export { DealerPanel };
